import { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';

function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'traveler',
    phone: '',
    age: '',
    has_valid_id: false,
    agrees_to_terms: false
  });

  const { login, register } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(formData);
      }

      if (result.success) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1 className="auth-title">
          {isLogin ? 'Welcome Back' : 'Join ZypeeTravel'}
        </h1>

        <div className="auth-tabs">
          <div 
            className={`auth-tab ${isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(true)}
          >
            Sign In
          </div>
          <div 
            className={`auth-tab ${!isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(false)}
          >
            Sign Up
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  className="form-input"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">I am a...</label>
                <select
                  name="role"
                  className="form-select"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <option value="traveler">Traveler (Looking for rentals)</option>
                  <option value="property_owner">Property Owner (Listing properties)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number (Optional)</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                />
              </div>

              {formData.role === 'traveler' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Age *</label>
                    <input
                      type="number"
                      name="age"
                      className="form-input"
                      value={formData.age}
                      onChange={handleChange}
                      required
                      min="18"
                      placeholder="Enter your age"
                    />
                    <small style={{ color: '#718096', fontSize: '0.875rem' }}>
                      Must be 21+ to book rentals
                    </small>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="has_valid_id"
                        checked={formData.has_valid_id}
                        onChange={handleChange}
                        style={{ width: 'auto' }}
                      />
                      <span className="form-label" style={{ margin: 0 }}>I have a valid ID for verification</span>
                    </label>
                  </div>
                </>
              )}

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="agrees_to_terms"
                    checked={formData.agrees_to_terms}
                    onChange={handleChange}
                    required
                    style={{ width: 'auto', marginTop: '4px' }}
                  />
                  <div>
                    <span className="form-label" style={{ margin: 0 }}>
                      I agree to the Terms of Service and Liability Disclaimer *
                    </span>
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: '#718096', 
                      marginTop: '4px',
                      lineHeight: '1.4'
                    }}>
                      I understand that ZypeeTravel is a marketplace platform and is not responsible 
                      for any injuries, theft, or incidents at properties. I book at my own risk.
                    </div>
                  </div>
                </label>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              name="email"
              className="form-input"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              name="password"
              className="form-input"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter your password"
              minLength="6"
            />
          </div>

          <button 
            type="submit" 
            className="auth-submit"
            disabled={loading}
          >
            {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        {!isLogin && (
          <div style={{ 
            marginTop: '24px', 
            padding: '16px', 
            background: '#f0f4f8', 
            borderRadius: '8px',
            fontSize: '0.875rem',
            color: '#4a5568'
          }}>
            <strong>Pricing:</strong>
            <ul style={{ margin: '8px 0 0 16px' }}>
              <li><strong>Free Loyalty Plan: $0/month</strong> (Basic features)</li>
              <li><strong>Premium Traveller's Plan: $9.99/month</strong> (AI Concierge, Restaurant recommendations, Premium perks)</li>
              <li style={{ marginTop: '8px', color: '#6b7280', fontSize: '0.8rem' }}><em>Optional Loyalty Add-ons:</em></li>
              <li style={{ marginLeft: '16px', color: '#6b7280' }}>+ Plus Add-on: $3.99/month (2X loyalty points)</li>
              <li style={{ marginLeft: '16px', color: '#6b7280' }}>+ SuperPlus Add-on: $5.99/month (3X loyalty points)</li>
              <li style={{ marginLeft: '16px', color: '#6b7280' }}>+ UltraPlus Add-on: $7.99/month (5X loyalty points)</li>
              <li style={{ marginTop: '8px' }}><strong>Booking Fees:</strong></li>
              <li style={{ marginLeft: '16px' }}>• Property owners: 10% service fee</li>
              <li style={{ marginLeft: '16px', color: '#dc2626', fontWeight: '600' }}>• Travelers: $100 essential cleaning fee per booking (non-refundable)</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthPage;